import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { searchTaskFiles, TaskFileSearchResult } from '../../services/api';

interface Props {
  taskId: string;
}

const TaskFileSearch: React.FC<Props> = ({ taskId }) => {
  const { t } = useTranslation('tasks');
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<'hybrid' | 'fulltext' | 'semantic'>('hybrid');
  const [results, setResults] = useState<TaskFileSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed) return;

    setLoading(true);
    setSearched(true);
    try {
      const response = await searchTaskFiles(taskId, trimmed, mode);
      setResults(response.results);
    } catch (err) {
      console.error('Task file search failed:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [taskId, query, mode]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
      <h4 className="text-sm font-medium text-gray-700 mb-2">
        {t('detail.fileSearch.title', 'Search Task Files')}
      </h4>

      {/* Search input + mode */}
      <div className="flex gap-2 mb-2">
        <div className="flex-1 relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('detail.fileSearch.placeholder', 'Search by content or meaning...')}
            className="w-full pl-10 pr-4 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none"
          />
        </div>
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as 'hybrid' | 'fulltext' | 'semantic')}
          className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-blue-200 outline-none"
        >
          <option value="hybrid">{t('detail.fileSearch.modeHybrid', 'Hybrid')}</option>
          <option value="fulltext">{t('detail.fileSearch.modeFulltext', 'Text')}</option>
          <option value="semantic">{t('detail.fileSearch.modeSemantic', 'Semantic')}</option>
        </select>
        <button
          onClick={handleSearch}
          disabled={!query.trim() || loading}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            t('detail.fileSearch.search', 'Search')
          )}
        </button>
      </div>

      {/* Results */}
      {searched && !loading && (
        <div className="mt-2">
          {results.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-3">
              {t('detail.fileSearch.noResults', 'No files match your search')}
            </p>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {results.map((r) => (
                <div key={r.file_id} className="flex items-center gap-2 px-3 py-2 bg-white rounded border border-gray-100 text-sm">
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span className="flex-1 truncate text-gray-800">{r.name}</span>
                  {r.hybrid_score != null && (
                    <span className="text-xs text-blue-500 flex-shrink-0">
                      {Math.round(r.hybrid_score * 100)}%
                    </span>
                  )}
                  {r.headline && (
                    <span className="text-xs text-gray-400 flex-shrink-0 truncate max-w-[120px]">{r.headline}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskFileSearch;
